import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Expander, ExpanderContent, Button, List, Bleed } from '@fs/zion-ui'
import styles from './ExpanderList.styles'
import trans from './ExpanderList.transl'

export const itemHeights = {
  listItem: 52,
  listItemDense: 40,
  chip: 44,
}
const ControlledExpanderList = ({
  as: TagName = List,
  children,
  itemHeight = itemHeights.listItemDense,
  visibleItemCount = 3,
  totalItemCount,
  open,
  setOpen,
  bleed,
  additionalHeight = 0, // Account for any extra static height, such as the checkboxlist label
}) => {
  const hasHiddenItems = totalItemCount > visibleItemCount
  const collapsedHeight =
    (hasHiddenItems ? itemHeight * visibleItemCount : itemHeight * totalItemCount) + additionalHeight
  let content = (
    <Expander open={open} setOpen={setOpen}>
      <ExpanderContent collapsedHeight={collapsedHeight}>
        <TagName>{children}</TagName>
      </ExpanderContent>
    </Expander>
  )
  if (bleed) {
    content = <Bleed sides>{content}</Bleed>
  }
  return (
    <div css={styles.container}>
      {content}
      {hasHiddenItems && (
        <div css={styles.expanderButton}>
          <Button keyline="left" emphasis="low" onClick={() => setOpen(!open)}>
            {open ? trans.showLessButton() : trans.showAllButton(totalItemCount)}
          </Button>
        </div>
      )}
    </div>
  )
}
const UncontrolledExpanderList = ({ open, ...rest }) => {
  const [uncontrolledOpen, setOpen] = useState(open)
  return <ControlledExpanderList {...{ ...rest, open: uncontrolledOpen, setOpen }} />
}
export function ExpanderList({ setOpen, ...rest }) {
  return setOpen ? (
    <ControlledExpanderList setOpen={setOpen} {...rest} />
  ) : (
    <UncontrolledExpanderList setOpen={setOpen} {...rest} />
  )
}
ExpanderList.propTypes = {
  /** The enclosing tag for your children */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  /** The content for the expanding list. */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /** The pixel height for one item in the Expander */
  itemHeight: PropTypes.number,
  /** The number of items visible all the time. Any more will be hidden inside the expander. */
  visibleItemCount: PropTypes.number,
  /** The total number of items in the content. This helps the Expander decide when to display the button. */
  totalItemCount: PropTypes.number,
  /** Whether or not the expander is open by default. */
  open: PropTypes.bool,
  /** For managing shared state of initially 'open' */
  setOpen: PropTypes.func,
}
